/**
 * Where each canvas object is actually edited (long-press / right-
 * click context menu -> Edit). Princewill's rule: Edit must take the
 * user to the real editor for that thing - the form field or panel
 * section that controls it - never a dead end. Objects with no edit
 * surface anywhere return null and the menu offers Delete only.
 *
 * `fieldId` is the DOM id of the form input to focus (ids are set in
 * CheckBuilder's Details panel). `canvasText` marks the user's own
 * text objects (uctx-*), whose editor IS the canvas: they are fabric
 * IText and their edits sync back to state on editing exit.
 */

export interface ObjectEditTarget {
    /** Tool-panel section id (see SECTION_TITLES in CheckBuilder). */
    section: string;
    /** Form field DOM id to focus after the section opens. */
    fieldId?: string;
    /** Edit = enter in-canvas text editing on the object itself. */
    canvasText?: boolean;
    /** Short human name for the object, shown in the menu header. */
    label: string;
}

const T = (section: string, label: string, fieldId?: string): ObjectEditTarget => ({ section, label, fieldId });

const FRONT: Record<string, ObjectEditTarget> = {
    bankDetails: T('check_info', 'Bank header', 'oc-field-bankName'),
    accHolderName: T('check_info', 'Account holder', 'oc-field-accountHolderName'),
    accHolderAddr: T('check_info', 'Account holder address', 'oc-field-accountHolderAddress'),
    einText: T('payroll_info', 'Company EIN', 'oc-field-ein'),
    checkNumber: T('check_info', 'Check number', 'oc-field-checkNumber'),
    fractionalRouting: T('check_info', 'Fractional routing', 'oc-field-fractionalRouting'),
    dateLabel: T('check_info', 'Date', 'oc-field-date'),
    dateValue: T('check_info', 'Date', 'oc-field-date'),
    dateLine: T('check_info', 'Date', 'oc-field-date'),
    amountBox: T('check_info', 'Amount', 'oc-field-amount'),
    amountSign: T('check_info', 'Amount', 'oc-field-amount'),
    amountValue: T('check_info', 'Amount', 'oc-field-amount'),
    amountWordsValue: T('check_info', 'Amount in words', 'oc-field-amount'),
    amountWordsLabel: T('check_info', 'Amount in words', 'oc-field-amount'),
    amountWordsSuffix: T('check_info', 'Amount in words', 'oc-field-amount'),
    amountWordsLine: T('check_info', 'Amount in words', 'oc-field-amount'),
    payToLabel1: T('check_info', 'Payee', 'oc-field-payeeName'),
    payToLabel2: T('check_info', 'Payee', 'oc-field-payeeName'),
    payeeNameVal: T('check_info', 'Payee', 'oc-field-payeeName'),
    payeeLine: T('check_info', 'Payee', 'oc-field-payeeName'),
    payeeAddress: T('check_info', 'Payee address', 'oc-field-payeeAddress'),
    memoLabel: T('check_info', 'Memo', 'oc-field-memo'),
    memoValue: T('check_info', 'Memo', 'oc-field-memo'),
    memoLine: T('check_info', 'Memo', 'oc-field-memo'),
    watermark: T('check_info', 'Watermark', 'oc-field-watermarkText'),
    micrLine: T('micr_config', 'MICR line'),
    customLogo: T('custom_assets', 'Bank logo'),
    customSignature: T('custom_assets', 'Signature'),
    signatureGroup2: T('custom_assets', 'Signature'),
    signatureLine: T('custom_assets', 'Signature'),
    signatureX: T('custom_assets', 'Signature'),
    signatureLabel: T('custom_assets', 'Signature'),
    signatureWarning: T('custom_assets', 'Signature'),
    microprintSigLine: T('custom_assets', 'Signature'),
    specialtyHeader: T('bank_style', 'Design header'),
    specialtySubHeader: T('bank_style', 'Design header'),
    treasuryOfficerLabel: T('bank_style', 'Design header'),
    warning_band: T('security', 'Security warning band'),
    topStatement: T('security', 'Top security statement'),
    karenBannerText: T('security', 'Banner security statement'),
    securityBandText: T('security', 'Security band statement'),
    thermoSeal: T('security', 'Thermochromic seal'),
    securitySquare: T('security', 'Security square'),
    heatReactiveMark: T('security', 'Heat-reactive ink mark'),
    templateWordmark: T('bank_style', 'Design wordmark'),
    padlockGroup: T('security', 'CPSA padlock'),
    padlockStatement: T('security', 'Padlock statement'),
    holoGroup: T('security', 'Holographic strip'),
    bofa_hologram: T('security', 'Hologram'),
    mpIndicator: T('security', 'Microprint indicator')
};

const BACK: Record<string, ObjectEditTarget> = {
    'back-bg-left': T('paper_texture', 'Back background'),
    'back-bg-right': T('paper_texture', 'Back background'),
    'back-imagematch': T('check_info', 'Deposit details'),
    'watermark-original': T('bank_style', 'Back watermark'),
    'watermark-document': T('bank_style', 'Back watermark'),
    'endorse-divider': T('check_info', 'Endorsement area'),
    'endorse-header': T('check_info', 'Endorsement area'),
    'endorse-x': T('check_info', 'Endorsement area'),
    'endorse-warning': T('check_info', 'Endorsement area'),
    'endorse-reserved': T('check_info', 'Endorsement area'),
    'strip-note': T('check_info', 'Endorsement area'),
    'mobile-instruction': T('check_info', 'Mobile deposit block'),
    'mobile-checkbox': T('check_info', 'Mobile deposit block'),
    'mobile-deposit-block': T('check_info', 'Mobile deposit block'),
    'deposit-only-name': T('check_info', 'Deposit-only block'),
    'deposit-only-label': T('check_info', 'Deposit-only block'),
    'deposit-only-account': T('check_info', 'Deposit-only block'),
    'echeck-instructions-frame': T('check_info', 'Back instructions'),
    'echeck-instructions-panel': T('check_info', 'Back instructions'),
    'security-panel': T('security', 'Security features panel'),
    'security-box-frame': T('security', 'Security features panel'),
    'security-box-inner-frame': T('security', 'Security features panel'),
    'security-regcc': T('security', 'Security features panel')
};

/** The edit target for a canvas object, or null when it has no editor. */
export function editTargetForObject(id: string | undefined | null, side: 'front' | 'back'): ObjectEditTarget | null {
    if (!id) return null;
    if (side === 'front' && id.startsWith('uctx-')) {
        return { section: 'custom_assets', label: 'Your text', canvasText: true };
    }
    const table = side === 'front' ? FRONT : BACK;
    if (table[id]) return table[id];
    if (side === 'back') {
        if (/^endorse-line-\d+$/.test(id)) return T('check_info', 'Endorsement area');
        if (id.startsWith('security-')) return T('security', 'Security features panel');
    }
    return null;
}

/** Menu header label for an object (its edit label, or a plain fallback). */
export function objectMenuLabel(id: string | undefined | null, side: 'front' | 'back'): string {
    const t = editTargetForObject(id, side);
    if (t) return t.label;
    return side === 'back' ? 'Back element' : 'Check element';
}
